import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  getReductionById,
  getReductionPdfBlob,
  getReductions,
  getReductionsByInjunctionId,
  getReductionsByImpositionId,
  getReductionsByPeriodId,
  openReductionDownload,
  updateReduction,
  type PaginatedResponse,
  type ReductionEvidenceListModel,
  type ReductionEvidenceModel,
  type UpdateReductionParams,
} from "@/api/cle-api";

import {
  mergeEvidenceDetailUpdate,
  reviewThreadAuditTrailQueryKey,
} from "./useReviewThread";

export type { UpdateReductionParams } from "@/api/cle-api";

export interface UseReductionsParams {
  userId?: string;
  page?: number;
  pageSize?: number;
  reported?: boolean;
}

export interface UseReductionParams {
  userId?: string;
  reductionId?: string;
}

export interface UseReductionsByPeriodParams {
  userId?: string;
  periodId?: string;
}

export interface UseReductionsByInjunctionParams {
  userId?: string;
  injunctionId?: string;
}

export interface UseReductionsByImpositionParams {
  userId?: string;
  impositionId?: string;
}

export interface DownloadReductionParams {
  userId: string;
  reductionId: string;
}

export const reductionsQueryKey = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: UseReductionsParams) =>
  ["cle-api", "users", userId, "reductions", page, pageSize, reported] as const;

export const reductionsByPeriodQueryKey = ({
  userId,
  periodId,
}: UseReductionsByPeriodParams) =>
  ["cle-api", "users", userId, "reductions", "by-period", periodId] as const;

export const reductionsByInjunctionQueryKey = ({
  userId,
  injunctionId,
}: UseReductionsByInjunctionParams) =>
  [
    "cle-api",
    "users",
    userId,
    "reductions",
    "by-injunction",
    injunctionId,
  ] as const;

export const reductionsByImpositionQueryKey = ({
  userId,
  impositionId,
}: UseReductionsByImpositionParams) =>
  [
    "cle-api",
    "users",
    userId,
    "reductions",
    "by-imposition",
    impositionId,
  ] as const;

export const reductionQueryKey = ({
  userId,
  reductionId,
}: UseReductionParams) =>
  ["cle-api", "users", userId, "reductions", reductionId] as const;

export const reductionPdfQueryKey = ({
  userId,
  reductionId,
}: UseReductionParams) =>
  ["cle-api", "users", userId, "reductions", reductionId, "pdf"] as const;

export const useReductions = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: UseReductionsParams): UseQueryResult<
  PaginatedResponse<ReductionEvidenceListModel>,
  Error
> =>
  useQuery({
    queryKey: reductionsQueryKey({ userId, page, pageSize, reported }),
    queryFn: () =>
      getReductions({
        userId: userId ?? "",
        page,
        pageSize,
        reported,
      }),
    enabled: !!userId,
  });

export const useReductionsByPeriod = ({
  userId,
  periodId,
}: UseReductionsByPeriodParams): UseQueryResult<
  ReductionEvidenceListModel[],
  Error
> =>
  useQuery({
    queryKey: reductionsByPeriodQueryKey({ userId, periodId }),
    queryFn: () =>
      getReductionsByPeriodId({
        userId: userId ?? "",
        periodId: periodId ?? "",
      }),
    enabled: !!userId && !!periodId,
  });

export const useReductionsByInjunction = ({
  userId,
  injunctionId,
}: UseReductionsByInjunctionParams): UseQueryResult<
  ReductionEvidenceListModel[],
  Error
> =>
  useQuery({
    queryKey: reductionsByInjunctionQueryKey({ userId, injunctionId }),
    queryFn: () =>
      getReductionsByInjunctionId({
        userId: userId ?? "",
        injunctionId: injunctionId ?? "",
      }),
    enabled: !!userId && !!injunctionId,
  });

export const useReductionsByImposition = ({
  userId,
  impositionId,
}: UseReductionsByImpositionParams): UseQueryResult<
  ReductionEvidenceListModel[],
  Error
> =>
  useQuery({
    queryKey: reductionsByImpositionQueryKey({ userId, impositionId }),
    queryFn: () =>
      getReductionsByImpositionId({
        userId: userId ?? "",
        impositionId: impositionId ?? "",
      }),
    enabled: !!userId && !!impositionId,
  });

export const useReduction = ({
  userId,
  reductionId,
}: UseReductionParams): UseQueryResult<ReductionEvidenceModel, Error> =>
  useQuery({
    queryKey: reductionQueryKey({ userId, reductionId }),
    queryFn: () =>
      getReductionById({
        userId: userId ?? "",
        reductionId: reductionId ?? "",
      }),
    enabled: !!userId && !!reductionId,
  });

export const useDownloadReduction = (): UseMutationResult<
  void,
  Error,
  DownloadReductionParams
> =>
  useMutation({
    mutationFn: openReductionDownload,
  });

export const useReductionPdf = ({
  userId,
  reductionId,
}: UseReductionParams): UseQueryResult<Blob, Error> =>
  useQuery({
    queryKey: reductionPdfQueryKey({ userId, reductionId }),
    queryFn: () =>
      getReductionPdfBlob({
        userId: userId ?? "",
        reductionId: reductionId ?? "",
      }),
    enabled: !!userId && !!reductionId,
  });

export const useUpdateReduction = (): UseMutationResult<
  ReductionEvidenceModel,
  Error,
  UpdateReductionParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateReduction,
    onSuccess: (data, { reductionId, userId }) => {
      queryClient.setQueryData<ReductionEvidenceModel>(
        reductionQueryKey({ userId, reductionId }),
        (old) => mergeEvidenceDetailUpdate(old, data),
      );
      void queryClient.invalidateQueries({
        queryKey: reviewThreadAuditTrailQueryKey(reductionId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["cle-api", "users", userId, "reductions"],
      });
    },
  });
};
